import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Avatar,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getCustomers, saveCustomer, deleteCustomer, updateCustomer } from '../../services/storage';
import type { Customer } from '../../types/api.types';

const ACCENT_COLORS = [
  '#1737bc', '#7b4f2e', '#2e6b3e', '#8b2e6b',
  '#3a7a28', '#b83232', '#c47a1a', '#2e5f8b',
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const EMPTY: Omit<Customer, 'id'> = {
  name: '', category: '', url: '', logo: '', initials: '', color: ACCENT_COLORS[0],
};

const AdminCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | number | null>(null);
  const [form, setForm] = useState<Omit<Customer, 'id'>>(EMPTY);
  const [deleteConfirm, setDeleteConfirm] = useState<Customer | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Logo input mode: 'url' or 'file'
  const [logoMode, setLogoMode] = useState<'url' | 'file'>('url');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = () => { getCustomers().then(setCustomers).catch(() => {}); };

  useEffect(() => { reload(); }, []);

  const resetLogoState = () => {
    setLogoMode('url');
    setLogoFile(null);
    setLogoPreview(null);
  };

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY);
    resetLogoState();
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (c: Customer) => {
    setEditId(c.id);
    setForm({
      name: c.name ?? '',
      category: c.category ?? '',
      url: c.url ?? '',
      logo: c.logo ?? '',
      initials: c.initials ?? '',
      color: c.color ?? ACCENT_COLORS[0],
    });
    resetLogoState();
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setForm((f) => ({ ...f, logo: '' }));
  };

  const handleLogoModeChange = (_: React.MouseEvent, mode: 'url' | 'file' | null) => {
    if (!mode) return;
    setLogoMode(mode);
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      const data: Omit<Customer, 'id'> = {
        ...form,
        initials: form.initials || initials(form.name),
        logo: logoMode === 'file'
          ? '' // will be replaced by uploaded file on server
          : form.logo || `https://www.google.com/s2/favicons?domain=${new URL(form.url.startsWith('http') ? form.url : `https://${form.url}`).hostname}&sz=64`,
      };
      if (editId !== null) {
        await updateCustomer(editId, data, logoFile ?? undefined);
      } else {
        await saveCustomer(data, logoFile ?? undefined);
      }
      reload();
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errors?: Record<string, string[]>; message?: string } } };
      const errors = axiosErr?.response?.data?.errors;
      if (errors) {
        setSaveError(Object.values(errors).flat().join(' · '));
      } else {
        setSaveError(axiosErr?.response?.data?.message ?? 'Unbekannter Fehler');
      }
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm) { await deleteCustomer(deleteConfirm.id); reload(); setDeleteConfirm(null); }
  };

  const field = (key: keyof Omit<Customer, 'id'>, label: string, required = false, placeholder = '') => (
    <TextField
      key={key}
      label={label}
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      fullWidth
      required={required}
      placeholder={placeholder}
      size="small"
    />
  );

  // Effective preview src: file preview > form logo url > favicon fallback
  const previewLogoSrc = logoPreview
    || form.logo
    || (form.url ? `https://www.google.com/s2/favicons?domain=${(() => { try { return new URL(form.url.startsWith('http') ? form.url : `https://${form.url}`).hostname; } catch { return ''; } })()}&sz=64` : '');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a2640' }}>Kunden</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Kunden die auf der Homepage angezeigt werden
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Kunde hinzufügen
        </Button>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase' }}>Logo</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase' }}>Kategorie</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase' }}>Website</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase' }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  Noch keine Kunden vorhanden
                </TableCell>
              </TableRow>
            )}
            {customers.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <CustomerAvatar customer={c} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={c.category} size="small" sx={{ fontSize: '0.72rem', bgcolor: `${c.color}18`, color: c.color, fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  <Box
                    component="a"
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1737bc', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {new URL(c.url.startsWith('http') ? c.url : `https://${c.url}`).hostname.replace('www.', '')}
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(c)} sx={{ mr: 0.5 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteConfirm(c)} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetLogoState(); setSaveError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Kunde bearbeiten' : 'Neuen Kunden hinzufügen'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {saveError && (
              <Box sx={{ p: 1.5, bgcolor: '#fdecea', borderRadius: 1, color: '#c62828', fontSize: '0.82rem' }}>
                {saveError}
              </Box>
            )}

            {field('name', 'Name', true, 'z.B. Coffee Fellows')}
            {field('category', 'Kategorie', true, 'z.B. Café & Gastronomie')}
            {field('url', 'Website URL', true, 'https://example.de')}

            {/* Logo section */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Logo
              </Typography>
              <ToggleButtonGroup
                value={logoMode}
                exclusive
                onChange={handleLogoModeChange}
                size="small"
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="url" sx={{ gap: 0.5, fontSize: '0.78rem', textTransform: 'none' }}>
                  <LinkIcon fontSize="small" /> URL eingeben
                </ToggleButton>
                <ToggleButton value="file" sx={{ gap: 0.5, fontSize: '0.78rem', textTransform: 'none' }}>
                  <UploadFileIcon fontSize="small" /> Bild hochladen
                </ToggleButton>
              </ToggleButtonGroup>

              {logoMode === 'url' ? (
                <TextField
                  label="Logo URL"
                  value={form.logo}
                  onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
                  fullWidth
                  size="small"
                  placeholder="Leer lassen = automatisch vom Favicon"
                  helperText="Optional — wird automatisch aus der Website-URL generiert"
                />
              ) : (
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Datei auswählen
                    </Button>
                    {logoPreview && (
                      <Box
                        component="img"
                        src={logoPreview}
                        sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, border: '1px solid #e0e0e0', p: 0.5 }}
                      />
                    )}
                    {logoFile && (
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {logoFile.name}
                      </Typography>
                    )}
                    {!logoFile && (
                      <Typography variant="caption" color="text.secondary">
                        PNG, JPG, SVG — max. 4 MB
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Kürzel"
                value={form.initials}
                onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value }))}
                size="small"
                sx={{ width: 100 }}
                placeholder="CF"
                helperText="Max. 2 Buchst."
                inputProps={{ maxLength: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Farbe (Fallback)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ACCENT_COLORS.map((col) => (
                    <Box
                      key={col}
                      onClick={() => setForm((f) => ({ ...f, color: col }))}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: col, cursor: 'pointer',
                        border: form.color === col ? '3px solid #1a2640' : '2px solid transparent',
                        transition: 'border 0.15s',
                      }}
                    />
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="input" type="color" value={form.color} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, color: e.target.value }))}
                      sx={{ width: 28, height: 28, border: 'none', borderRadius: '50%', cursor: 'pointer', p: 0 }} />
                    <Typography variant="caption" color="text.secondary">Eigene</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Preview */}
            {form.name && (
              <Box sx={{ p: 2, bgcolor: '#f8f8f8', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Vorschau:</Typography>
                <CustomerAvatarPreview logoSrc={previewLogoSrc} form={form} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{form.category}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); resetLogoState(); setSaveError(null); }} color="inherit">Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.name || !form.category || !form.url}
          >
            {editId ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Kunde löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du <strong>{deleteConfirm?.name}</strong> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">Abbrechen</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────

const CustomerAvatar: React.FC<{ customer: Customer }> = ({ customer }) => {
  const [failed, setFailed] = React.useState(false);
  return failed || !customer.logo ? (
    <Avatar sx={{ width: 36, height: 36, bgcolor: `${customer.color}20`, color: customer.color, fontSize: '0.75rem', fontWeight: 800 }}>
      {customer.initials}
    </Avatar>
  ) : (
    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f5f5f5' }}>
      <Box component="img" src={customer.logo} alt={customer.name} onError={() => setFailed(true)} sx={{ width: 24, height: 24, objectFit: 'contain' }} />
    </Avatar>
  );
};

const CustomerAvatarPreview: React.FC<{ logoSrc: string; form: Omit<Customer, 'id'> }> = ({ logoSrc, form }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [logoSrc]);
  return failed || !logoSrc ? (
    <Avatar sx={{ width: 36, height: 36, bgcolor: `${form.color}20`, color: form.color, fontSize: '0.75rem', fontWeight: 800 }}>
      {form.initials || initials(form.name)}
    </Avatar>
  ) : (
    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f5f5f5' }}>
      <Box component="img" src={logoSrc} alt={form.name} onError={() => setFailed(true)} sx={{ width: 24, height: 24, objectFit: 'contain' }} />
    </Avatar>
  );
};

export default AdminCustomers;
